import React, { useEffect, useState } from 'react';
import { SortCondition } from 'shared/enums';
import { dataForOvertimeTable, sortingDate } from 'shared/helpers/utils';
import { usePullSort } from 'shared/hooks';
import { Badge, Spin } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { OvertimeStatuses } from 'shared/enums/overtime';
import CommonTable from 'features/tables/commonTable';
import { TCommonColumns } from 'shared/types/overtime.types';
import { useLazyGetOvertimeQuery } from 'app/services/edpApiV1/overtime';
import SearchComponent from 'shared/components/search/rvd-overtime';
import { ISearchData } from 'shared/types/rvd.types';
import { useAppSelector } from 'app/store';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';
import { EditOutlined } from '@ant-design/icons';
import ModalChangeResponsible from '../overtimePull/modalChangeResponsible';
import style from './index.module.scss';

const OvertimeSubstitutions = () => {
  const [search, setSearch] = useState<ISearchData>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [sortInfo, trigger] = usePullSort();
  const [getOvertimes, { data: overtimes, isFetching, isLoading }] = useLazyGetOvertimeQuery();
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const [requestId, setRequestId] = useState<number>();
  const [openModal, setOpenModal] = useState(false);
  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });

  const navigate = useNavigate();

  useEffect(() => {
    getOvertimes({
      page: currentPage,
      size: currentSize,
      orderDate: sortInfo.value ? sortInfo.value : undefined,
      type: 'other',
      allAvailableOrganizations,
    });
  }, [getOvertimes, currentPage, currentSize, sortInfo.value, allAvailableOrganizations]);

  useEffect(() => {
    const newData = {};
    Object.keys(search).forEach(key => {
      if (search[key]) {
        newData[key] = search[key];
      }
    });
    getOvertimes({ page: currentPage, size: currentSize, type: 'other', allAvailableOrganizations, ...newData });
  }, [currentPage, currentSize, getOvertimes, search, allAvailableOrganizations]);

  const onSearch = (data: ISearchData) => {
    setSearch({ ...data });
  };

  const onclickToRow = (id: number) => {
    navigate(`/overtime/update/${id}`);
  };

  const handleChangeResponsible = (event: React.MouseEvent, id: number): void => {
    event.stopPropagation();
    setRequestId(id);
    setOpenModal(true);
  };

  const columns: TCommonColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: true,
      onHeaderCell: _ => ({
        onClick: () => trigger(),
      }),
    },
    {
      title: 'Количество сотрудников',
      dataIndex: 'count',
      key: 'count',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span>
          <Badge status={status === OvertimeStatuses.LOADED_CPRODUCT ? 'default' : 'processing'} />
          {status}
        </span>
      ),
    },
    {
      title: 'Ответственный HR',
      dataIndex: 'responsible',
      key: 'responsible',
      render: (responsible, record: { key: React.Key }) => {
        return (
          <p className={style}>
            <span>{`${responsible}`}</span>
            <EditOutlined
              className={style.btnChangeResponsible}
              onClick={(e: React.MouseEvent) => handleChangeResponsible(e, Number(record.key))}
            />
          </p>
        );
      },
    },
    {
      title: 'Комментарий',
      dataIndex: 'userComment',
      key: 'userComment',
      render: (userComment: string) => {
        return (
          <div title={userComment} className="rvd-user-comment">
            {userComment}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SearchComponent onSearch={onSearch} />
      {allSubordinates && allSubordinates.length > 0 && requestId && (
        <ModalChangeResponsible
          allSubordinates={allSubordinates}
          openModal={openModal}
          setOpenModal={setOpenModal}
          requestId={requestId}
        />
      )}
      <Spin spinning={isLoading && isFetching}>
        <CommonTable
          dataSource={dataForOvertimeTable(overtimes?.data || [])}
          columns={columns}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={overtimes?.pagination.total}
          setCurrentSize={setCurrentSize}
          currentSize={currentSize}
          onclickToRow={(id: number) => onclickToRow(id)}
        />
      </Spin>
    </>
  );
};

export default OvertimeSubstitutions;
